if (!customElements.get('ocs-suggested-accessories')) {
  function getCarouselPageIndex(currentIndex, slideCount, scrollCount) {
    const lastPageIndex = Math.ceil(slideCount / scrollCount) - 1;
    if (currentIndex + scrollCount >= slideCount) return lastPageIndex;
    return Math.max(0, Math.min(Math.floor(currentIndex / scrollCount), lastPageIndex));
  }

  function getWrappedPageTarget(direction, currentPageIndex, slideCount, scrollCount, directionalLoop = false) {
    if (slideCount <= scrollCount) return null;

    const pageCount = Math.ceil(slideCount / scrollCount);
    const pageIndex = Math.max(0, Math.min(currentPageIndex, pageCount - 1));
    if (direction === 'previous') {
      if (pageIndex <= 0) return directionalLoop ? -1 : (pageCount - 1) * scrollCount;
      return (pageIndex - 1) * scrollCount;
    }
    if (direction === 'next') {
      if (pageIndex >= pageCount - 1) return directionalLoop ? slideCount : 0;
      return (pageIndex + 1) * scrollCount;
    }
    return null;
  }

  const debugLog = (message, details = {}) => {
    console.info(`[OCS Suggested Accessories] ${message}`, details);
  };

  const getCarouselDebugState = (slideshow, carousel, scrollCountOverride) => {
    const slides = Array.from(slideshow.querySelectorAll('[ref="slides[]"]'));
    const dots = Array.from(slideshow.querySelectorAll('[data-page-dot]'));
    const prev = /** @type {HTMLButtonElement|null} */ (slideshow.querySelector('[ref="previous"]'));
    const next = /** @type {HTMLButtonElement|null} */ (slideshow.querySelector('[ref="next"]'));
    const cssColumnCount = carousel
      ? parseInt(getComputedStyle(carousel).getPropertyValue('--column-count').trim(), 10)
      : NaN;
    const scrollCount = Number.isFinite(scrollCountOverride) && scrollCountOverride >= 1
      ? scrollCountOverride
      : (!isNaN(cssColumnCount) && cssColumnCount >= 1 ? cssColumnCount : 1);
    const visiblePills = dots.filter(dot => dot.closest('li')?.style.display !== 'none').length;
    const currentIndex = /** @type {any} */ (slideshow).current ?? 0;

    return {
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      carouselClass: carousel?.className || '',
      panelId: slideshow.closest('[role="tabpanel"]')?.id || null,
      cssColumnCount: isNaN(cssColumnCount) ? null : cssColumnCount,
      activeScrollCount: scrollCount,
      currentIndex,
      totalSlides: slides.length,
      renderedPills: dots.length,
      visiblePills,
      hiddenPills: Math.max(dots.length - visiblePills, 0),
      expectedVisiblePills: Math.ceil(slides.length / scrollCount),
      prevDisabled: prev?.disabled ?? null,
      nextDisabled: next?.disabled ?? null,
      selectedPillIndexes: dots
        .map((dot, index) => dot.getAttribute('aria-selected') === 'true' ? index : null)
        .filter(index => index !== null)
    };
  };

  window.ocsSuggestedAccessoriesDebugDump = () => {
    const states = Array.from(document.querySelectorAll('ocs-suggested-accessories slideshow-component'))
      .map((slideshow, index) => {
        const carousel = slideshow.closest('.ocs-suggested-accessories__carousel');
        return {
          index,
          ...getCarouselDebugState(slideshow, carousel)
        };
      });

    console.info('[OCS Suggested Accessories] Manual debug dump', states);
    return states;
  };

  customElements.define('ocs-suggested-accessories', class extends HTMLElement {
    #handleTabClick = (/** @type {Event} */ e) => {
      const activeTab = e.currentTarget;
      this.querySelectorAll('[role="tab"]').forEach(tab => {
        tab.classList.toggle('ocs-suggested-accessories__tab--active', tab === activeTab);
        tab.setAttribute('aria-selected', String(tab === activeTab));
      });
      this.querySelectorAll('[role="tabpanel"]').forEach(panel => {
        panel.classList.remove('ocs-suggested-accessories__panel--active');
      });
      const panel = this.querySelector('#' + activeTab.getAttribute('aria-controls'));
      if (panel) panel.classList.add('ocs-suggested-accessories__panel--active');

      debugLog('Tab selected', {
        tabText: activeTab.textContent?.trim() || '',
        controls: activeTab.getAttribute('aria-controls'),
        activePanelFound: Boolean(panel)
      });

      requestAnimationFrame(() => window.ocsSuggestedAccessoriesDebugDump?.());
    };

    // Patch each slideshow-component to scroll by the live column count per click.
    // --column-count changes at CSS breakpoints so we track it via ResizeObserver.
    // Arrow disabled state is handled both by the native slideshow (which now works
    // correctly because overflow: visible is no longer set on slideshow-container)
    // and by our updateArrows() for immediate consistency after dot/scroll events.
    #patchSlideshows() {
      const baseScrollCount = parseInt(this.dataset.scrollCount || '1', 10);
      const slideshows = this.querySelectorAll('slideshow-component');

      debugLog('Patching slideshows', {
        baseScrollCount,
        slideshowCount: slideshows.length,
        tabCount: this.querySelectorAll('[role="tab"]').length,
        panelCount: this.querySelectorAll('[role="tabpanel"]').length
      });

      slideshows.forEach((slideshow, carouselIndex) => {
        const carousel = slideshow.closest('.ocs-suggested-accessories__carousel');
        const wrapsAtEveryViewport = this.dataset.wrapNavigation === 'true';
        const wrapsOnDesktop = this.dataset.wrapNavigationDesktop === 'true';

        // scrollCount is mutable — ResizeObserver updates it when the breakpoint changes.
        let scrollCount = baseScrollCount;

        const totalSlides = () => slideshow.querySelectorAll('[ref="slides[]"]').length;

        const getDots = () => slideshow.querySelectorAll('[data-page-dot]');

        const updateArrows = (/** @type {number} */ currentIndex) => {
          const prev = /** @type {HTMLButtonElement|null} */ (slideshow.querySelector('[ref="previous"]'));
          const next = /** @type {HTMLButtonElement|null} */ (slideshow.querySelector('[ref="next"]'));
          const wraps = slideshow.hasAttribute('infinite');
          if (prev) prev.disabled = !wraps && currentIndex <= 0;
          if (next) next.disabled = !wraps && currentIndex + scrollCount >= totalSlides();
        };

        // Show only the dots that correspond to real pages at the current column
        // count. Extra dots (rendered server-side for max possible pages) are hidden.
        const updateDots = () => {
          const pageCount = Math.ceil(totalSlides() / scrollCount);
          const dots = getDots();
          const dotsContainer = dots[0]?.closest('.slideshow-controls__dots');
          if (dotsContainer) dotsContainer.style.display = pageCount <= 1 ? 'none' : '';
          dots.forEach((dot, i) => {
            const li = dot.closest('li');
            if (li) li.style.display = i < pageCount ? '' : 'none';
          });
        };

        // Read --column-count from the carousel's computed style and refresh state.
        const updateScrollCount = () => {
          if (carousel) {
            const col = parseInt(
              getComputedStyle(carousel).getPropertyValue('--column-count').trim(),
              10
            );
            if (!isNaN(col) && col >= 1) scrollCount = col;
          }
          const canWrap =
            totalSlides() > scrollCount &&
            (wrapsAtEveryViewport || (wrapsOnDesktop && window.matchMedia('(min-width: 750px)').matches));
          slideshow.toggleAttribute('infinite', canWrap);
          slideshow.toggleAttribute('smooth-loop', canWrap && wrapsAtEveryViewport && scrollCount === 1);
          updateDots();
          updateArrows(/** @type {any} */ (slideshow).current ?? 0);

          debugLog('Carousel state after init/resize', {
            carouselIndex,
            ...getCarouselDebugState(slideshow, carousel, scrollCount)
          });
        };

        // Own arrow navigation so every click advances exactly one logical page.
        // Horizon handles on:click at the slideshow ancestor during capture, before
        // a listener on the button can cancel it. Leaving that attribute in place
        // advances once natively and once here, skipping every other page.
        for (const [selector, direction] of [
          ['[ref="previous"]', 'previous'],
          ['[ref="next"]', 'next'],
        ]) {
          const arrow = slideshow.querySelector(selector);
          arrow?.removeAttribute('on:click');
          arrow?.addEventListener('click', (event) => {
            const selectedPageIndex = Array.from(getDots()).findIndex(
              (dot) => dot.getAttribute('aria-selected') === 'true'
            );
            const currentPageIndex = selectedPageIndex >= 0
              ? selectedPageIndex
              : getCarouselPageIndex((/** @type {any} */ (slideshow).current ?? 0), totalSlides(), scrollCount);
            const pageCount = Math.ceil(totalSlides() / scrollCount);
            let targetIndex = null;

            if (slideshow.hasAttribute('infinite')) {
              targetIndex = getWrappedPageTarget(
                direction,
                currentPageIndex,
                totalSlides(),
                scrollCount,
                scrollCount === 1,
              );
            } else if (direction === 'previous' && currentPageIndex > 0) {
              targetIndex = (currentPageIndex - 1) * scrollCount;
            } else if (direction === 'next' && currentPageIndex < pageCount - 1) {
              targetIndex = (currentPageIndex + 1) * scrollCount;
            }

            if (targetIndex === null) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            /** @type {any} */ (slideshow).select(targetIndex, event);
          }, { capture: true });
        }

        // Override dot clicks: baked-in on:click="/select/N×step" is wrong at non-desktop
        // breakpoints. Capture phase fires before Horizon's bubble-phase handler.
        getDots().forEach((dot, i) => {
          dot.removeAttribute('on:click');
          dot.addEventListener('click', (e) => {
            e.stopImmediatePropagation();
            debugLog('Pill clicked', {
              carouselIndex,
              pillIndex: i,
              targetSlideIndex: i * scrollCount,
              ...getCarouselDebugState(slideshow, carousel, scrollCount)
            });
            /** @type {any} */ (slideshow).select(i * scrollCount);
          }, { capture: true });
        });

        // Sync page-based dots and arrow state on every slide change.
        // data-page-dot buttons have no ref="dots[]" so the slideshow base class
        // never touches them — we own their aria-selected entirely.
        slideshow.addEventListener('slideshow:select', (e) => {
          const index = /** @type {CustomEvent} */ (e).detail?.index ?? 0;
          const dots = getDots();
          if (dots.length) {
            const pageIndex = getCarouselPageIndex(index, totalSlides(), scrollCount);
            dots.forEach((dot, i) => dot.setAttribute('aria-selected', String(i === pageIndex)));
          }
          updateArrows(index);

          debugLog('Slide selected', {
            carouselIndex,
            selectedSlideIndex: index,
            selectedPageIndex: getCarouselPageIndex(index, totalSlides(), scrollCount),
            ...getCarouselDebugState(slideshow, carousel, scrollCount)
          });
        });

        // Re-derive scrollCount whenever the carousel resizes (breakpoint change).
        if (typeof ResizeObserver !== 'undefined' && carousel) {
          new ResizeObserver(updateScrollCount).observe(carousel);
        }

        // Defer so CSS --column-count is computed before we read it.
        requestAnimationFrame(updateScrollCount);
      });
    }

    connectedCallback() {
      debugLog('Connected', {
        dataScrollCount: this.dataset.scrollCount || null,
        tabCount: this.querySelectorAll('[role="tab"]').length,
        panelCount: this.querySelectorAll('[role="tabpanel"]').length,
        slideshowCount: this.querySelectorAll('slideshow-component').length
      });

      this.querySelectorAll('[role="tab"]').forEach(tab => {
        tab.addEventListener('click', this.#handleTabClick);
      });

      customElements.whenDefined('slideshow-component').then(() => {
        if (this.isConnected) this.#patchSlideshows();
      });
    }

    disconnectedCallback() {
      this.querySelectorAll('[role="tab"]').forEach(tab => {
        tab.removeEventListener('click', this.#handleTabClick);
      });
    }
  });
}
